import { getAuthHeaders } from "../utils/authHeaders";
import { API_URL } from "../config/api";

export interface ApplicationData {
  jobId: string;
  coverLetter: string;
  resumeUrl?: string;
  availability: "Immediately" | "1 month" | "2 months" | "3 months";
  expectedSalary?: string;
  additionalInfo?: string;
}

export interface Application {
  _id: string;
  userId: string | {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber?: string;
    profession?: string;
    specialization?: string;
  };
  jobId: string | {
    _id: string;
    title: string;
    company: string;
    location: string;
    salary: string;
    logoUrl: string;
    status: string;
  };
  coverLetter: string;
  resumeUrl?: string;
  availability: string;
  expectedSalary?: string;
  additionalInfo?: string;
  status: "pending" | "accepted" | "rejected";
  employerNotes?: string;
  appliedAt: string;
  reviewedAt?: string;
  createdAt: string;
  updatedAt: string;
}

// Submit a new application
export const submitApplication = async (data: ApplicationData): Promise<Application> => {
  try {

    const response = await fetch(`${API_URL}/applications`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(),
      },
      credentials: "include",
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to submit application");
    }

    const result = await response.json();
    return result.application;
  } catch (error) {
    console.error("❌ Error submitting application:", error);
    throw error;
  }
};

// Get all applications for the current user (employee)
export const getUserApplications = async (): Promise<Application[]> => {
  try {
    console.log("📋 Fetching user applications...");

    const response = await fetch(`${API_URL}/applications/user`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(),
      },
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error("Failed to fetch applications");
    }

    const data = await response.json();
    console.log("✅ User applications fetched:", data.length, "applications");
    return data;
  } catch (error) {
    console.error("❌ Error fetching applications:", error);
    throw error;
  }
};

// Get all applications for a specific job (employer)
export const getJobApplications = async (jobId: string): Promise<Application[]> => {
  try {

    const response = await fetch(`${API_URL}/applications/job/${jobId}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(),
      },
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error("Failed to fetch job applications");
    }

    const data = await response.json();
    console.log("✅ Job applications fetched:", data.length, "applications");
    return data;
  } catch (error) {
    console.error("❌ Error fetching job applications:", error);
    throw error;
  }
};

// Get all applications for employer's jobs
export const getEmployerApplications = async (): Promise<Application[]> => {
  try {
    console.log("📋 Fetching all employer applications...");

    const response = await fetch(`${API_URL}/applications/employer`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(),
      },
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error("Failed to fetch employer applications");
    }

    const data = await response.json();
    console.log("✅ Employer applications fetched:", data.length, "applications");
    return data;
  } catch (error) {
    console.error("❌ Error fetching employer applications:", error);
    throw error;
  }
};

// Get single application details
export const getApplicationById = async (id: string): Promise<Application> => {
  try {

    const response = await fetch(`${API_URL}/applications/${id}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(),
      },
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error("Failed to fetch application");
    }

    const data = await response.json();
    console.log("✅ Application details fetched");
    return data;
  } catch (error) {
    console.error("❌ Error fetching application:", error);
    throw error;
  }
};

// Check if user has applied for a job
export const checkApplicationStatus = async (jobId: string): Promise<{ hasApplied: boolean; application: Application | null }> => {
  try {

    const response = await fetch(`${API_URL}/applications/check/${jobId}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(),
      },
      credentials: "include",
    });

    if (!response.ok) {
      return { hasApplied: false, application: null };
    }

    const data = await response.json();
    console.log("✅ Application status:", data.hasApplied ? "Applied" : "Not applied");
    return data;
  } catch (error) {
    console.error("❌ Error checking application status:", error);
    return { hasApplied: false, application: null };
  }
};

// Update application status (employer)
export const updateApplicationStatus = async (
  applicationId: string,
  status: Application["status"],
  employerNotes?: string
): Promise<Application> => {
  try {

    const response = await fetch(`${API_URL}/applications/${applicationId}/status`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(),
      },
      credentials: "include",
      body: JSON.stringify({ status, employerNotes }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to update application status");
    }

    const result = await response.json();
    console.log("✅ Application status updated");
    return result.application;
  } catch (error) {
    console.error("❌ Error updating application status:", error);
    throw error;
  }
};

// Withdraw application (employee)
export const withdrawApplication = async (applicationId: string): Promise<void> => {
  try {

    const response = await fetch(`${API_URL}/applications/${applicationId}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(),
      },
      credentials: "include",
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to withdraw application");
    }

    console.log("✅ Application withdrawn successfully");
  } catch (error) {
    console.error("❌ Error withdrawing application:", error);
    throw error;
  }
};
